// Resource-to-transform registry for the app data provider wrapper.
// Read transforms: enrich records (e.g., compute pricing).
// Write transforms: strip relation fields and generated columns before mutations.
//
// Per-entity metadata (TRANSACTION_RELATIONS, TRANSACTION_GENERATED, etc.)
// lives next to the entity type in shared/types/* rather than here so the
// strip list stays in sync with the App interface.

import { enrichTransaction } from "@shared/transforms/transaction";
import { WITH_CHECKS_ITEMS } from "@shared/checks/checkRegistry";
import {
  TRANSACTION_RELATIONS,
  TRANSACTION_GENERATED,
  TRANSPORT_RELATIONS,
} from "@shared/types/transaction";
import type { RecordTransform, TransformRegistry, WriteTransform } from "./appDataProvider";

// Computed (non-base-table) columns of transactions_with_checks, derived from
// the select-list ledger so the set can't drift from the view: every `tc`/`tf`
// entry plus each raw entry's trailing `AS "alias"`. Filters on these force
// the view's laterals anyway, so two-phase getList falls back to one fetch.
const WITH_CHECKS_COMPUTED_FIELDS: readonly string[] = WITH_CHECKS_ITEMS.flatMap((item) => {
  if (item.col) return item.src === "t" ? [] : [item.col];
  const alias = /AS "([a-z0-9_]+)"\s*$/i.exec(item.raw ?? "");
  return alias ? [alias[1]] : [];
});

/** Transaction resources (tables/views) that should have pricing computed on read. */
const TRANSACTION_RESOURCES = [
  "transactions",
  "transactions_with_checks",
  "transactions_edpr_view",
] as const;

/**
 * Strip all non-DB fields from a record before writing.
 * Removes: joined relations, computed fields, read-only fields, generated columns.
 */
function stripNonDbFields(knownRelations: string[], generatedColumns: string[] = []) {
  return (values: Record<string, unknown>): Record<string, unknown> => {
    const cleaned = { ...values };
    // Remove relation fields (objects from joins)
    for (const key of knownRelations) delete cleaned[key];
    // Remove generated / read-only columns
    for (const key of generatedColumns) delete cleaned[key];
    // Remove common read-only fields
    delete cleaned.id;
    delete cleaned.created_at;
    delete cleaned.updated_at;
    return cleaned;
  };
}

// ── Write transforms per resource ────────────────────────────────────────

const stripTransactionTransport = stripNonDbFields(
  [...TRANSPORT_RELATIONS],
  ["search_vector"],
);

const stripTransaction = stripNonDbFields(
  [...TRANSACTION_RELATIONS],
  [...TRANSACTION_GENERATED],
);

const stripTransactionFile = stripNonDbFields(
  ["transaction"],
);

// ── Company sub-entity write transforms ──────────────────────────────────

const stripContact = stripNonDbFields(
  [],
  ["search_vector"],
);

const stripCompanyBankAccount = stripNonDbFields([]);

const stripCompanySalesAssignment = stripNonDbFields(
  ["employee", "product", "company"],
);

const stripCompanyWasteProperty = stripNonDbFields(
  ["company_documents"],
);

const stripCompanyRecyclingProcess = stripNonDbFields(
  ["company_documents", "recycling_processes"],
);

const stripCompanyDocument = stripNonDbFields(
  ["companies"],
  // _pendingFile is a UI-only draft field used by CompanyDocumentsSection to
  // defer the file upload until save. It must never reach Postgres.
  ["_pendingFile"],
);

const stripPriceList = stripNonDbFields(
  [
    "product",
    "products",
    "company",
    "companies",
    "load_place",
    "load_place_company",
    "load_place_companies",
    "unload_place",
    "unload_place_company",
    "unload_place_companies",
  ],
  ["search_vector"],
);

const stripEdprDocument = stripNonDbFields(
  [
    "organizacja_odzysku_company",
    "edpr_records",
    "edpr_requests",
  ],
);

const stripEdprRecord = stripNonDbFields(
  [
    "transactions",
    "load_commercial_company",
    "unload_commercial_company",
    "unload_place_company",
    "exporter_company",
    "importer_company",
    "invoices",
    "edpr_documents",
  ],
);

/** Build the full transform registry. */
export function buildAppTransforms(): TransformRegistry {
  const read: Record<string, RecordTransform> = {};
  for (const resource of TRANSACTION_RESOURCES) {
    // Registry boundary: the wrapper casts the record back to the caller's type.
    read[resource] = enrichTransaction as RecordTransform;
  }

  const write: Record<string, WriteTransform> = {
    transactions: stripTransaction,
    transaction_transports: stripTransactionTransport,
    transaction_files: stripTransactionFile,
    contacts: stripContact,
    company_bank_accounts: stripCompanyBankAccount,
    company_sales_assignments: stripCompanySalesAssignment,
    company_waste_properties: stripCompanyWasteProperty,
    company_recycling_processes: stripCompanyRecyclingProcess,
    company_documents: stripCompanyDocument,
    price_lists: stripPriceList,
    edpr_documents: stripEdprDocument,
    edpr_records: stripEdprRecord,
  };

  return {
    read,
    write,
    // transactions_with_checks is an expensive view (per-row lateral aggregates);
    // fetching only ids for the page then full rows by id avoids forcing a
    // full materialization for every list query. See appDataProvider.ts.
    twoPhaseList: ["transactions_with_checks"],
    twoPhaseComputedFields: { transactions_with_checks: WITH_CHECKS_COMPUTED_FIELDS },
  };
}
